import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { errorDialogSeen } from "@/analytics/events/error";
import { trackEvent } from "@/analytics/track";
import { ApplicationError } from "@/error/ApplicationError";
import { errorMessage, errorToString } from "@/error/errorUnwrapper";
import { isRecord } from "@/utils/record";
import type { UnknownRecord } from "@/utils/type";
import { asReadonly } from "@/utils/vue";

export interface ErrorDetails {
  time: string;
  shortMessage: string;
  correlationId: string;
}

export const useGlobalErrorStore = defineStore("globalError", () => {
  const globalError = ref<{ error: unknown; timestamp: number; message?: string } | null>(null);

  const errorDetails = computed(() => {
    if (!globalError.value) return null;

    const error = globalError.value.error;
    const appError = error instanceof ApplicationError ? error : null;

    // Extract the error message (can be nested in different places, depending on the shape of error)
    const shortMessage = errorToString(
      error,
      errorMessage(error, appError?.name || globalError.value.message || "Unknown"),
    );

    // Extract the correlation ID (it can also be in different places)
    const contexts = appError?.contexts || {};
    const correlationId = (contexts.request?.correlationId ||
      headers(contexts.cause?.config)?.["correlation-id"] ||
      "") as string;

    const time = new Date(globalError.value.timestamp).toISOString();

    return { time, shortMessage, correlationId };
  });

  function setGlobalError(error: unknown, message?: string) {
    globalError.value = { error, timestamp: Date.now(), message };
    trackEvent(errorDialogSeen());
  }

  return { globalError: asReadonly(globalError), errorDetails, setGlobalError };
});

function headers(config: unknown): UnknownRecord | undefined {
  return isRecord(config) && isRecord(config.headers) ? config.headers : undefined;
}
